import { differenceInDays, formatISO, format } from 'date-fns';

import { useRouter } from 'next/router';
import { GAEventName } from 'analytics/ga-event-name.enum';
import { changeOrderDateEvent, subscriptionShipmentDateClickEvent } from 'analytics/events';
import { useMutationUpdateSubscriptionDetailsV2 } from 'state/internal/updateSubscriptionDetails';

import { useStoreValue } from './useStore';
import { useDateFormatting } from './useDateFormatString';
import { useCreatePerformanceTimer } from '../../shared/hooks/useCreatePerformanceTimer';

interface ChangeOrderDateV2Props {
  contractId: number;
  oldOrderDate: Date;
  newOrderDate: Date;
}

export const useChangeOrderDateV2 = (isRebill: boolean = false) => {
  const router = useRouter();
  const { mutate, isPending, isSuccess } = useMutationUpdateSubscriptionDetailsV2();
  const store = useStoreValue();
  const getFormattedDateLong = useDateFormatting();
  const createPerformanceTimer = useCreatePerformanceTimer();

  const onChangeOrderDate = ({
    contractId,
    oldOrderDate,
    newOrderDate
  }: ChangeOrderDateV2Props) => {
    const timer = createPerformanceTimer(
      isRebill ? 'rebillDelayShipment' : 'membersSubscriptionChangeNextOrderDate',
      {
        membersSubscriptionOldDate: oldOrderDate,
        membersSubscriptionNewDate: newOrderDate
      }
    );

    mutate(
      {
        contractId,
        data: {
          next_order_date: format(new Date(newOrderDate), 'yyyy-MM-dd')
        }
      },
      {
        onSuccess: () => {
          timer.success();
          subscriptionShipmentDateClickEvent({
            subscriptionId: contractId,
            checkoutType: 'unified',
            dateDifference: differenceInDays(newOrderDate, oldOrderDate),
            newDate: formatISO(newOrderDate),
            oldDate: formatISO(oldOrderDate),
            location: router.asPath
          });
          if (isRebill) {
            changeOrderDateEvent({
              eventName: GAEventName.REBILL_DELAY,
              orderId: contractId,
              date: getFormattedDateLong(newOrderDate),
              store
            });
          }
        },
        onError: (error) => {
          timer.failure(error);
        }
      }
    );
  };

  return { onChangeOrderDate, isPending, isSuccess };
};
